import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { validateEmail } from '../../util';
import { useNotification } from '../../components/notifications/useNotification';
import FormButton from '../../components/FormButton/FormButton';
import FormField from '../../components/FormField/FormField';
import axios from 'axios';
import useDocumentMeta from '../../utils/useDocumentMeta';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const notification = useNotification();
    useDocumentMeta('seo-forgot-title', 'seo-forgot-desc', { canonicalPath: '/forgot-password' });

    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [oauthOnly, setOauthOnly] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setOauthOnly(false);

        // Client-side validation
        if (!email) {
            const msg = t('errorMessages.emailRequired');
            setError(msg);
            notification.showWarning(msg);
            return;
        }
        if (!validateEmail(email)) {
            const msg = t('errorMessages.emailInvalid');
            setError(msg);
            notification.showWarning(msg);
            return;
        }

        try {
            setLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/forgot-password`,
                { email },
                { withCredentials: true }
            );
            setSent(true);
            notification.showSuccess(t('fp-email-sent'));
        } catch (err) {
            if (err?.response?.status === 409 && err?.response?.data?.code === 'OAUTH_ONLY') {
                setOauthOnly(true);
            } else if (err?.response?.status === 404 && err?.response?.data?.code === 'NOT_FOUND') {
                const msg = t('fp-email-not-found');
                setError(msg);
                notification.showWarning(msg);
            } else if (err?.response?.status === 429) {
                notification.showWarning(t('errorMessages.too-many-requests'));
            } else {
                notification.showError(t('errorMessages.unknown-error'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fp">
            <div className="fp-card">

                {/* ── Top bar ── */}
                <div className="fp-topbar">
                    <button
                        className="fp-back-btn"
                        type="button"
                        onClick={() => navigate('/login')}
                        aria-label="Go back"
                    >
                        ←
                    </button>
                    <span className="fp-brand">{t('lp-brand')}</span>
                </div>

                {/* ── Header ── */}
                <div className="fp-header">
                    <h1 className="fp-title">{t('fp-title')}</h1>
                    <p className="fp-subtitle">{t('fp-subtitle')}</p>
                </div>

                {/* ── OAuth-only warning ── */}
                {oauthOnly && (
                    <div className="fp-warning">
                        <span className="fp-warning-icon">⚠️</span>
                        <p>{t('fp-oauth-only-warning')}</p>
                    </div>
                )}

                {/* ── Success message ── */}
                {sent && !oauthOnly && (
                    <div className="fp-success">
                        <span className="fp-success-icon">✉️</span>
                        <p>{t('fp-email-sent-description')}</p>
                    </div>
                )}

                {/* ── Form ── */}
                {!sent && !oauthOnly && (
                    <form className="fp-form" onSubmit={handleSubmit} noValidate>
                        <FormField
                            label={t('lp-email-label')}
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setError(''); }}
                            placeholder={t('lp-email-placeholder')}
                            icon="✉️"
                            error={error}
                            maxLength={254}
                            autoComplete="email"
                        />

                        <FormButton
                            type="submit"
                            fullWidth
                            loading={loading}
                            loadingText={t('sending')}
                        >
                            {t('fp-send-link-btn')}
                        </FormButton>
                    </form>
                )}

                {/* ── Back to login ── */}
                <div className="fp-footer">
                    <p>
                        {t('fp-remember-password')}
                        <Link to="/login">{t('fp-back-to-login')}</Link>
                    </p>
                </div>

            </div>
        </div>
    );
};

export default ForgotPasswordPage;
